import { Model, Factory, belongsTo, trait, hasMany } from 'miragejs'
import type { ModelDefinition, ModelInstance } from 'miragejs/-types'
import {
  randAnimal,
  randBoolean,
  randChanceBoolean,
  randNumber,
  randWord,
  randParagraph,
  randRecentDate,
  randPastDate,
  rand,
  randAlphaNumeric,
  randImg,
} from '@ngneat/falso'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'

import type { ClientData } from '@/models/client.js'
import type { MirageServer, MockServer } from './server.js'
import { type ListItemsData, generateBreed } from './ListItem.js'
import {
  sortable,
  pageable,
  getQueryParamValue,
  createErrorResponse,
} from './utils.js'
import {
  formatToDateTimeISO,
  formatToDateTimeISODate,
} from '@/utils/date/conversion.js'
import {
  isAfter,
  isBefore,
  isSame,
  isValidDate,
} from '@/utils/date/validation.js'
import { ListItemsTypes } from '@/composables/listItems.js'
import type { PatientData } from '@/composables/patients.js'
import {
  AvailablePatientSpecies,
  type PatientSpecies,
} from '@/composables/usePatientDetails.js'
export type PatientDataWithRelations = PatientData & {
  client: ModelInstance<ClientData>
}

export const generatePatientSpeciesIcon = (species: string) => {
  if (!species) {
    return 'generic-pet-paw'
  }

  const speciesIcons = {
    Cat: 'generic-feline', // cat, lynx, leopard, ocelot
    Dog: 'generic-canine', // dog, fox, wolf, jackal, coyote
    Bird: 'generic-birds', // parakeets, lovebirds, canaries, parrots
    Rabbit: 'generic-leporidae', // rabbit, hare
    Horse: 'generic-equidae', // horse, donky, mule, zebra
  }
  return speciesIcons?.[species as PatientSpecies] || 'generic-pet-paw'
}

export const model = (Model as ModelDefinition<PatientData>).extend({
  client: belongsTo('client'),
  cprStatus: belongsTo('patientCPRStatusEntry'),
  activeHealthPlans: hasMany('patientHealthPlan'),
  insuranceCompany: belongsTo('listItem'),
  mergePatient: belongsTo('patient', { inverse: null }),
  invoiceRows: hasMany('invoiceRow', { inverse: 'patient' }),
  tags: hasMany('tag', { inverse: null }),
  microchipCheck: belongsTo('microchipCheck', { inverse: null }),
  homeDepartment: belongsTo('department', { inverse: null }),
})

export const factory = Factory.extend<PatientData>({
  name() {
    return randAnimal()
  },
  official_name() {
    return rand([`${randAnimal()} officially`, undefined])
  },
  date_of_birth() {
    return rand([randPastDate({ years: 15 }).toISOString(), null, undefined])
  },
  breed() {
    return rand([null, undefined])
  },
  species() {
    return rand([...AvailablePatientSpecies, undefined])
  },
  species_icon_class() {
    return generatePatientSpeciesIcon(this.species as string)
  },
  archived() {
    return randBoolean()
  },
  gender() {
    return this.species === 'Horse'
      ? rand(['MARE', 'GELDING', 'STALLION'])
      : rand(['UNKNOWN', 'MALE', 'FEMALE', 'MALE_FIXED', 'FEMALE_FIXED'])
  },
  microchip() {
    return rand([
      // Ideally a string that complies the ISO 11784 (15 chars length)
      randAlphaNumeric({ length: 15 }).join(''),
      null,
      undefined,
    ])
  },
  microchip_date() {
    return this.microchip
      ? formatToDateTimeISODate(randPastDate())
      : rand([null, undefined])
  },
  weight() {
    return randChanceBoolean({ chanceTrue: 0.8 })
      ? `${randNumber({ min: 1, max: 99 })}.${randNumber({
          min: 1,
          max: 99,
        })} lbs`
      : null
  },
  weighted_at() {
    return this.weight
      ? randRecentDate().toISOString()
      : rand([null, undefined])
  },
  insurance() {
    return rand([`POL-${randNumber({ min: 10000, max: 99999 })}`, null])
  },
  tags_string() {
    return rand([randWord({ length: 10 }).join(','), undefined])
  },
  critical_notes() {
    return rand([randParagraph({ maxCharCount: 100 }), undefined])
  },
  picture_thumb_url() {
    return rand([
      randImg({ width: 64, height: 64, category: 'home,pet' }),
      null,
      undefined,
    ])
  },
  picture_medium_url() {
    return rand([
      randImg({ width: 256, height: 256, category: 'home,pet' }),
      null,
      undefined,
    ])
  },
  afterCreate(patient, server) {
    if (!patient.client) {
      patient.update({
        client: server.create('client', 'withPhoneNumbers', 'withTags'),
      })
    }
  },
  withRandomTags: trait({
    afterCreate(patient) {
      if (!patient.tags_string && randChanceBoolean({ chanceTrue: 0.5 })) {
        patient.update({ tags_string: randWord({ length: 10 }).join(',') })
      }
    },
  }),
  withTags: trait({
    afterCreate(patient, server) {
      if (!patient.tags?.length) {
        const tagTexts = server.createList(
          'tagText',
          randNumber({ min: 2, max: 3 }),
        )

        const tags = tagTexts.map((tagText) =>
          server.create('tag', { tagText }),
        )
        patient.update({ tags })
      }
    },
  }),
  withCPRStatus: trait({
    afterCreate(patient, server) {
      if (!patient.cprStatus) {
        patient.update({ cprStatus: server.create('patientCprStatusEntry') })
      }
    },
  }),
  withHealthPlans: trait({
    afterCreate(patient, server) {
      if (!patient.activeHealthPlans.length) {
        patient.update({
          activeHealthPlans: server.createList(
            'patientHealthPlan',
            randNumber({ min: 1, max: 5 }),
          ),
        })
      }
    },
  }),
  withUnarchived: trait({
    afterCreate(patient) {
      patient.update({ archived: false })
    },
  }),
  withMergePatient: trait({
    afterCreate(patient, server) {
      if (!patient.mergePatient) {
        patient.update({ mergePatient: server.create('patient') })
      }
    },
  }),
  withInsuranceCompany: trait({
    afterCreate(patient, server) {
      if (!patient.insuranceCompany) {
        patient.update({
          insuranceCompany: server.create('listItem', 'asInsuranceCompany'),
          insurance: `POL-${randNumber({ min: 10000, max: 99999 })}`,
        })
      }
    },
  }),
  withMicrochip: trait({
    afterCreate(patient) {
      patient.update({
        microchip: randAlphaNumeric({ length: 15 }).join(''),
        microchip_date: formatToDateTimeISODate(randPastDate()),
      })
    },
  }),
  withNotChipped: trait({
    afterCreate(patient) {
      patient.update({
        microchip: null,
        microchip_date: null,
      })
    },
  }),
  withBreed: trait({
    afterCreate(patient, server) {
      const existingBreeds = server.schema
        .all('listItem')
        .models.filter((item) =>
          (item.attrs as ListItemsData).code?.includes(ListItemsTypes.Breed),
        )
      const breed =
        existingBreeds.length > 0
          ? rand(existingBreeds)
          : server.create('listItem', 'asBreed')

      if (
        patient.species &&
        (breed.attrs as ListItemsData).code?.includes(ListItemsTypes.Breed)
      ) {
        /**
         * if the patient has a species set, then the breed should be updated
         * accordingly, otherwise the breed is randomly generated
         */
        breed.update({
          code: `${patient.species}-${ListItemsTypes.Breed}-${randAlphaNumeric({ length: 5 }).join('')}`,
          label: generateBreed[patient.species as PatientSpecies](),
        })
      }
      patient.update({ breed: (breed.attrs as ListItemsData).label })
    },
  }),
  withCalendarAppointments: trait({
    afterCreate(patient, server) {
      server.createList('calendarAppointment', 2, {
        patients: [patient],
        client: patient.client,
      })
    },
  }),
  withReminder: trait({
    afterCreate(patient, server) {
      const reminderTemplate =
        server.schema.first('reminderTemplate') ||
        server.create('reminderTemplate')
      server.create('reminder', { patient, reminderTemplate })
    },
  }),
  withTasks: trait({
    afterCreate(patient, server) {
      server.create('task', 'withFutureDue', {
        patients: [patient],
      })
      server.create('task', {
        due: null,
        patients: [patient],
      })
    },
  }),
})

export function routes(server: MockServer) {
  server.resource('patient')

  server.get('/patients', function (this: MirageServer, schema) {
    return schema.all('patient')
  })

  server.get(
    '/patients/check_duplicate',
    function (this: MirageServer, schema, request) {
      const microchip = getQueryParamValue(request.queryParams['microchip'])

      if (!microchip) {
        return { data: [], meta: { pagination: { count: 0, pages: 1 } } }
      }

      const filtered = schema.where('patient', (patient: PatientData) =>
        patient.microchip
          ? patient.microchip.toLowerCase() === microchip.toLowerCase()
          : false,
      )

      const { data, meta } = pageable(filtered, request)

      return {
        ...this.serialize(data),
        meta: { pagination: meta },
      }
    },
    { timing: 1500 },
  )

  server.get('/patients/:id', function (schema, request) {
    const id = request.params.id
    const patient = schema.find('patient', id)

    if (!patient) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Patient with id ${id} not found` }],
      })
    }

    return patient
  })

  // Microchip routes
  server.post('/patients/:id/add_microchip', function (schema, request) {
    const id = request.params.id
    const patient = schema.find('patient', id)

    if (!patient) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Patient with id ${id} not found` }],
      })
    }

    let requestBody
    try {
      requestBody = JSON.parse(request.requestBody)
    } catch {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Invalid JSON body' }],
      })
    }
    const attributes = requestBody.data?.attributes || {}
    const microchipId = attributes.microchip_id

    const now = getTodayDateTimeISO()

    // If microchip_id is null/empty, mark as not chipped
    if (!microchipId) {
      // Create MicrochipCheck record
      const microchipCheck = schema.create('microchipCheck', {
        microchip_id: null,
        status: 'NOT_MICROCHIPPED',
        created: now,
        modified: now,
      })

      // Update patient
      patient.update({
        microchip: null,
        microchip_date: null,
        microchipCheck,
      })

      return {
        data: {
          id: microchipCheck.id,
          type: 'MicrochipCheck',
          attributes: {
            microchip_id: null,
            status: 'NOT_MICROCHIPPED',
            created: now,
            modified: now,
          },
        },
      }
    }

    // Otherwise, set the microchip
    const microchipCheck = schema.create('microchipCheck', {
      microchip_id: microchipId,
      status: 'VALID',
      created: now,
      modified: now,
    })

    patient.update({
      microchip: microchipId,
      microchip_date: attributes.microchip_date || null,
      microchipCheck,
    })

    return {
      data: {
        id: microchipCheck.id,
        type: 'MicrochipCheck',
        attributes: {
          microchip_id: microchipId,
          status: 'VALID',
          created: now,
          modified: now,
        },
      },
    }
  })

  server.post('/patients/:id/verify_microchip', function (schema, request) {
    const id = request.params.id
    const patient = schema.find('patient', id)

    if (!patient) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Patient with id ${id} not found` }],
      })
    }

    let requestBody
    try {
      requestBody = JSON.parse(request.requestBody)
    } catch {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Invalid JSON body' }],
      })
    }
    const microchipId = (
      requestBody.data?.attributes?.microchip_id ?? ''
    ).trim()

    if (!microchipId) {
      return createErrorResponse({
        status: 400,
        errors: [
          {
            detail: 'microchip_id is required',
            pointer: '/data/attributes/microchip_id',
          },
        ],
      })
    }

    // Check if any other patient has this microchip
    const allPatients = schema.all('patient')
    const isDuplicate = allPatients.models.some(
      (p) =>
        p.id !== id && p.attrs.microchip && p.attrs.microchip === microchipId,
    )

    return {
      data: {
        id: patient.id,
        type: 'PatientMicrochipVerify',
        attributes: {
          status: isDuplicate ? 'DUPLICATED' : 'VALID',
        },
      },
    }
  })

  server.get(
    '/dev-playground/patients',
    function (this: MirageServer, schema, request) {
      // 🚨 Fake endpoint – only for development purposes.
      // This endpoint is used exclusively for testing and demonstrating advanced filtering for Tables in the Dev Playground.
      // It leverages the Patients Model to simulate real data, allowing developers to play around with filtering the table's content,
      // by search, status, gender, species, along with sorting.

      // 1) Sort
      const patients = schema.all('patient')
      const sorted = sortable(patients, request, {
        'date_of_birth.range': (item) => {
          const isoDate = item.attrs?.date_of_birth
            ? formatToDateTimeISO(item.attrs.date_of_birth) || ''
            : ''
          return isValidDate(isoDate) ? isoDate : -Infinity
        },
      })
      let filtered = sorted

      // 2) Filter
      // 2.1) Date range filtering - Date of Birth
      const dateOfBirthRange = getQueryParamValue(
        request?.queryParams?.['filter[date_of_birth.range]'],
      ) as string | undefined
      if (dateOfBirthRange?.length) {
        const [startRaw, endRaw] = dateOfBirthRange.split(',')
        const startDate = startRaw?.trim()
        const endDate = endRaw?.trim()
        if (startDate && endDate) {
          filtered = filtered.filter((item) => {
            if (!item.attrs.date_of_birth) {
              return false
            }

            const dob = item.attrs.date_of_birth
            return (
              (!isBefore(dob, startDate) || isSame(dob, startDate, 'day')) &&
              (!isAfter(dob, endDate) || isSame(dob, endDate, 'day'))
            )
          })
        }
      }

      // 2.2) Search Filter
      const search = getQueryParamValue(
        request?.queryParams?.['filter[search]'],
      )
      if (search) {
        filtered = filtered.filter((item) => {
          const name = item.attrs.name?.toLowerCase() || ''
          return name.includes(search?.toLowerCase() || '')
        })
      }

      // 2.3) Status Filter
      const status = getQueryParamValue(
        request?.queryParams?.['filter[status.in]'],
      )
      if (status) {
        const statusArray = status.split(',').map((s) => s.trim().toUpperCase())

        filtered = filtered.filter((item) =>
          statusArray.some(
            (statusValue) =>
              (statusValue === 'ARCHIVED' && item.attrs.archived) ||
              (statusValue === 'ACTIVE' && !item.attrs.archived),
          ),
        )
      }

      // 2.4) Gender Filter
      const gender = getQueryParamValue(
        request?.queryParams?.['filter[gender.in]'],
      )
      if (gender) {
        filtered = filtered.filter((item) => {
          const itemGender = item.attrs.gender?.toLowerCase()
          const genderArray = gender?.toLowerCase().split(',') || ['']
          return genderArray.some((genderValue) => itemGender === genderValue)
        })
      }

      // 3) Paginate
      const { data, meta } = pageable(filtered, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )
}

export function seeds(server: MockServer) {
  server.createList('patient', 10, 'withTags')
  server.createList(
    'patient',
    10,
    'withBreed',
    'withCPRStatus',
    'withHealthPlans',
    'withInsuranceCompany',
    'withMergePatient',
    'withTags',
  )
}
